import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, CheckSquare, Gift, Clock } from 'lucide-react';
import { get } from '@/shared/api/api_client';
import { Button, Card } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatRelativeTime } from '@/shared/utils/format';
import type { ChoreCompletion } from '@/entities/chore';
import type { Redemption } from '@/entities/reward';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface ApprovalQueueProps {
  familyId: string;
  onApprove: (completionId: string) => void;
  onReject: (completionId: string, note: string) => void;
}

interface PendingChoresResponse {
  completions: ChoreCompletion[];
}

interface PendingRedemptionsResponse {
  redemptions: Redemption[];
}

type ActiveTab = 'chores' | 'rewards';

// ---------------------------------------------------------------------------
// Inline rejection form
// ---------------------------------------------------------------------------
function RejectForm({
  id,
  onSubmit,
  onCancel,
}: {
  id: string;
  onSubmit: (note: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [note, setNote] = useState('');

  return (
    <div className="mt-2 flex flex-col gap-2">
      <textarea
        id={`reject-note-${id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Reason for rejection (optional)..."
        rows={2}
        className={cn(
          'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900',
          'placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-danger-400',
          'focus:border-danger-400 transition-colors duration-150 resize-none',
        )}
        aria-label="Rejection note"
      />
      <div className="flex gap-2">
        <Button
          variant="danger"
          size="sm"
          onClick={() => onSubmit(note)}
          className="flex-1"
        >
          Confirm Reject
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chore completion item
// ---------------------------------------------------------------------------
function ChoreQueueItem({
  completion,
  onApprove,
  onReject,
}: {
  completion: ChoreCompletion;
  onApprove: () => void;
  onReject: (note: string) => void;
}): React.ReactElement {
  const [showRejectForm, setShowRejectForm] = useState(false);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900">{completion.choreName}</p>
          <p className="mt-0.5 text-sm text-slate-600">{completion.childName}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-slate-400">
          <Clock size={12} aria-hidden="true" />
          {formatRelativeTime(completion.submittedAt)}
        </div>
      </div>

      {!showRejectForm ? (
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1 bg-secondary-600 hover:bg-secondary-700 focus-visible:ring-secondary-500"
            onClick={onApprove}
            aria-label={`Approve ${completion.choreName} for ${completion.childName}`}
          >
            <CheckCircle2 size={14} aria-hidden="true" />
            Approve
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="flex-1"
            onClick={() => setShowRejectForm(true)}
            aria-label={`Reject ${completion.choreName} for ${completion.childName}`}
          >
            <XCircle size={14} aria-hidden="true" />
            Reject
          </Button>
        </div>
      ) : (
        <RejectForm
          id={completion.id}
          onSubmit={(note) => {
            onReject(note);
            setShowRejectForm(false);
          }}
          onCancel={() => setShowRejectForm(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Redemption item
// ---------------------------------------------------------------------------
function RedemptionQueueItem({
  redemption,
  onApprove,
  onReject,
}: {
  redemption: Redemption;
  onApprove: () => void;
  onReject: (note: string) => void;
}): React.ReactElement {
  const [showRejectForm, setShowRejectForm] = useState(false);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900">{redemption.rewardName}</p>
          <p className="mt-0.5 text-sm text-slate-600">
            {redemption.childName} • {redemption.pointCost.toLocaleString()} pts
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-slate-400">
          <Clock size={12} aria-hidden="true" />
          {formatRelativeTime(redemption.requestedAt)}
        </div>
      </div>

      {!showRejectForm ? (
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1 bg-secondary-600 hover:bg-secondary-700 focus-visible:ring-secondary-500"
            onClick={onApprove}
            aria-label={`Approve ${redemption.rewardName} for ${redemption.childName}`}
          >
            <CheckCircle2 size={14} aria-hidden="true" />
            Approve
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="flex-1"
            onClick={() => setShowRejectForm(true)}
            aria-label={`Reject ${redemption.rewardName} for ${redemption.childName}`}
          >
            <XCircle size={14} aria-hidden="true" />
            Reject
          </Button>
        </div>
      ) : (
        <RejectForm
          id={redemption.id}
          onSubmit={(note) => {
            onReject(note);
            setShowRejectForm(false);
          }}
          onCancel={() => setShowRejectForm(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
function EmptyTab({ label }: { label: string }): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      <CheckCircle2 size={32} className="text-secondary-400" aria-hidden="true" />
      <p className="text-sm font-medium text-slate-600">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApprovalQueue component
// ---------------------------------------------------------------------------
export default function ApprovalQueue({
  familyId,
  onApprove,
  onReject,
}: ApprovalQueueProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<ActiveTab>('chores');

  const { data: choresData, isLoading: choresLoading } = useQuery<PendingChoresResponse>({
    queryKey: ['completions', familyId, 'pending'],
    queryFn: () => get<PendingChoresResponse>(`/families/${familyId}/completions/pending`),
    enabled: Boolean(familyId),
  });

  const { data: redemptionsData, isLoading: redemptionsLoading } = useQuery<PendingRedemptionsResponse>({
    queryKey: ['redemptions', familyId, 'pending'],
    queryFn: () => get<PendingRedemptionsResponse>(`/families/${familyId}/redemptions/pending`),
    enabled: Boolean(familyId),
  });

  const pendingChores = choresData?.completions ?? [];
  const pendingRedemptions = redemptionsData?.redemptions ?? [];

  const tabs: Array<{ id: ActiveTab; label: string; count: number; icon: React.ReactNode }> = [
    {
      id: 'chores',
      label: 'Chores',
      count: pendingChores.length,
      icon: <CheckSquare size={15} aria-hidden="true" />,
    },
    {
      id: 'rewards',
      label: 'Rewards',
      count: pendingRedemptions.length,
      icon: <Gift size={15} aria-hidden="true" />,
    },
  ];

  const totalPending = pendingChores.length + pendingRedemptions.length;

  return (
    <Card className="flex flex-col gap-0" padding="sm">
      {/* Header */}
      <div className="flex items-center justify-between px-1 pb-3">
        <h2 className="text-base font-bold text-slate-900">
          Pending Approvals
          {totalPending > 0 && (
            <span
              className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-danger-500 px-1.5 text-[11px] font-bold text-white"
              aria-label={`${totalPending} pending`}
            >
              {totalPending}
            </span>
          )}
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200" role="tablist" aria-label="Approval categories">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
              activeTab === tab.id
                ? 'border-b-2 border-primary-600 text-primary-700'
                : 'border-b-2 border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count > 0 && (
              <span
                className={cn(
                  'inline-flex h-4.5 min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold',
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-slate-100 text-slate-600',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div className="pt-4">
        {/* Chores panel */}
        <div
          id="tabpanel-chores"
          role="tabpanel"
          aria-labelledby="tab-chores"
          hidden={activeTab !== 'chores'}
        >
          {choresLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse rounded-xl border border-slate-100 bg-slate-50 p-4 h-24" />
              ))}
            </div>
          ) : pendingChores.length === 0 ? (
            <EmptyTab label="No pending chore approvals" />
          ) : (
            <div className="space-y-3">
              {pendingChores.map((completion) => (
                <ChoreQueueItem
                  key={completion.id}
                  completion={completion}
                  onApprove={() => onApprove(completion.id)}
                  onReject={(note) => onReject(completion.id, note)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Rewards panel */}
        <div
          id="tabpanel-rewards"
          role="tabpanel"
          aria-labelledby="tab-rewards"
          hidden={activeTab !== 'rewards'}
        >
          {redemptionsLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse rounded-xl border border-slate-100 bg-slate-50 p-4 h-24" />
              ))}
            </div>
          ) : pendingRedemptions.length === 0 ? (
            <EmptyTab label="No pending reward redemptions" />
          ) : (
            <div className="space-y-3">
              {pendingRedemptions.map((redemption) => (
                <RedemptionQueueItem
                  key={redemption.id}
                  redemption={redemption}
                  onApprove={() => onApprove(redemption.id)}
                  onReject={(note) => onReject(redemption.id, note)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
